import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/hooks/useAuth";
import { auditDB, policiesDB, type AuditEntry, type DBPolicy } from "@/lib/db";
import { useRealtime } from "@/hooks/useRealtime";
import { Shield, Eye, Filter, Plus, Trash2 } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/Skeleton";
import { toast } from "sonner";
import { notifyError } from "@/lib/errorToast";

export function Governance() {
  const { user, isAdmin } = useAuth();
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [policies, setPolicies] = useState<DBPolicy[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [newName, setNewName] = useState("");
  const [newLimit, setNewLimit] = useState("10");

  const refresh = useCallback(async () => {
    if (!user) return;
    try {
      const [a, p] = await Promise.all([auditDB.feed(200), policiesDB.list()]);
      setEntries(a);
      setPolicies(p);
    } catch (e) {
      notifyError(e as Error, "Falha ao carregar governance");
    }
    setLoading(false);
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Realtime: qualquer mudança nas 3 origens recarrega o feed
  useRealtime("sandbox_runs", refresh);
  useRealtime("workflow_runs", refresh);
  useRealtime("chat_messages", refresh);

  const filtered = entries.filter((r) =>
    !filter || r.label.toLowerCase().includes(filter.toLowerCase()) || r.source.includes(filter.toLowerCase()),
  );

  async function addPolicy() {
    if (!newName) return toast.error("Nome obrigatório");
    try {
      await policiesDB.create({
        name: newName,
        policy_type: "cost_limit",
        config: { max_per_day_usd: Number(newLimit) || 10 },
      });
      toast.success("Policy criada");
      setNewName("");
      refresh();
    } catch (e) {
      notifyError(e as Error, "Falha ao criar policy");
    }
  }

  async function togglePolicy(id: string, enabled: boolean) {
    try {
      await policiesDB.toggle(id, enabled);
      refresh();
    } catch (e) {
      notifyError(e as Error, "Falha ao alternar");
    }
  }

  async function removePolicy(id: string) {
    try {
      await policiesDB.remove(id);
      toast.success("Policy removida");
      refresh();
    } catch (e) {
      notifyError(e as Error, "Falha");
    }
  }

  return (
    <div className="h-full overflow-auto p-6 space-y-6">
      <div>
        <h2 className="text-2xl font-display font-bold flex items-center gap-2">
          <Shield className="w-6 h-6 text-secondary" /> Governance
        </h2>
        <p className="text-sm text-muted-foreground">
          Audit log unificado · policies persistidas {isAdmin ? "· admin view" : "· seu escopo"}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="glass-panel p-5 rounded-lg border border-primary/20">
          <h3 className="text-xs font-mono uppercase text-muted-foreground mb-2">Audit entries</h3>
          {loading ? <Skeleton className="h-9 w-20" /> : (
            <>
              <div className="text-3xl font-bold font-display">{entries.length}</div>
              <div className="text-xs text-muted-foreground">
                {entries.filter((r) => r.status === "success" || r.status === "completed").length} ok ·{" "}
                {entries.filter((r) => r.status === "failed").length} fail
              </div>
            </>
          )}
        </div>
        <div className="glass-panel p-5 rounded-lg border border-primary/20">
          <h3 className="text-xs font-mono uppercase text-muted-foreground mb-2">Active policies</h3>
          {loading ? <Skeleton className="h-9 w-20" /> : (
            <>
              <div className="text-3xl font-bold font-display">{policies.filter((p) => p.enabled).length}</div>
              <div className="text-xs text-muted-foreground">{policies.length} totais</div>
            </>
          )}
        </div>
        <div className="glass-panel p-5 rounded-lg border border-primary/20">
          <h3 className="text-xs font-mono uppercase text-muted-foreground mb-2">Compliance</h3>
          <div className="text-3xl font-bold font-display text-success">100%</div>
          <div className="text-xs text-muted-foreground">RLS · SHA-512 · realtime</div>
        </div>
      </div>

      {/* Policies CRUD */}
      <div className="glass-panel rounded-lg border border-primary/20">
        <div className="p-4 border-b border-border flex items-center gap-3 flex-wrap">
          <Shield className="w-4 h-4 text-primary" />
          <h3 className="text-sm font-semibold flex-1">Policies</h3>
          <div className="flex items-end gap-2">
            <div>
              <Label className="text-[10px]">Nome</Label>
              <Input value={newName} onChange={(e) => setNewName(e.target.value)} className="h-8 w-40" placeholder="Daily cost cap" aria-label="Nome da policy" />
            </div>
            <div>
              <Label className="text-[10px]">Limit ($/day)</Label>
              <Input value={newLimit} onChange={(e) => setNewLimit(e.target.value)} className="h-8 w-24" type="number" aria-label="Limite diário" />
            </div>
            <Button size="sm" onClick={addPolicy} className="h-8 gap-1" aria-label="Criar policy">
              <Plus className="w-3 h-3" /> Add
            </Button>
          </div>
        </div>
        <div className="max-h-[260px] overflow-auto">
          {loading ? (
            <div className="p-4 space-y-2">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
            </div>
          ) : policies.length === 0 ? (
            <p className="text-xs text-muted-foreground py-8 text-center">Nenhuma policy ainda.</p>
          ) : (
            <table className="w-full text-xs">
              <tbody>
                {policies.map((p) => (
                  <tr key={p.id} className="border-t border-border/50 hover:bg-surface-2/50">
                    <td className="p-2 font-mono">{p.name}</td>
                    <td className="p-2 text-muted-foreground">{p.policy_type}</td>
                    <td className="p-2 text-muted-foreground font-mono text-[10px]">
                      ${(p.config?.max_per_day_usd as number) ?? 0}/day
                    </td>
                    <td className="p-2 w-20">
                      <Switch checked={p.enabled} onCheckedChange={(v) => togglePolicy(p.id, v)} aria-label={`Toggle ${p.name}`} />
                    </td>
                    <td className="p-2 w-12">
                      <button onClick={() => removePolicy(p.id)} aria-label={`Remover ${p.name}`} className="text-muted-foreground hover:text-destructive">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Audit log unificado */}
      <div className="glass-panel rounded-lg border border-primary/20">
        <div className="p-4 border-b border-border flex items-center gap-3">
          <Eye className="w-4 h-4 text-primary" />
          <h3 className="text-sm font-semibold flex-1">Audit log (live · sandbox + workflows + chat)</h3>
          <div className="relative">
            <Filter className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter..."
              aria-label="Filtrar audit"
              className="h-8 pl-8 pr-3 text-xs rounded bg-surface-2 border border-border w-48"
            />
          </div>
        </div>
        <div className="max-h-[400px] overflow-auto">
          {loading ? (
            <div className="p-4 space-y-2">
              {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-6 w-full" />)}
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-xs text-muted-foreground py-12 text-center">Sem entradas.</p>
          ) : (
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-surface-2 text-[10px] font-mono uppercase text-muted-foreground">
                <tr>
                  <th className="text-left p-2">Time</th>
                  <th className="text-left p-2">Source</th>
                  <th className="text-left p-2">Label</th>
                  <th className="text-left p-2">Status</th>
                  <th className="text-right p-2">Duration</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} className="border-t border-border/50 hover:bg-surface-2/50">
                    <td className="p-2 text-muted-foreground">{formatDistanceToNow(new Date(r.created_at), { addSuffix: true })}</td>
                    <td className="p-2 font-mono">
                      <span className="px-1.5 py-0.5 rounded bg-primary/10 text-primary text-[9px]">{r.source}</span>
                    </td>
                    <td className="p-2 font-mono truncate max-w-[200px]">{r.label}</td>
                    <td className="p-2">
                      <span className={`px-1.5 py-0.5 rounded text-[9px] ${
                        r.status === "success" || r.status === "completed"
                          ? "bg-success/10 text-success"
                          : r.status === "failed"
                          ? "bg-destructive/10 text-destructive"
                          : "bg-muted/10 text-muted-foreground"
                      }`}>
                        {r.status}
                      </span>
                    </td>
                    <td className="p-2 text-right font-mono">{r.duration_ms ?? "—"}ms</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
